import produce, { castDraft } from 'immer';
import { useRecoilCallback } from 'recoil';

import { FileReference, UploadingFile } from '../../../types/Files';
import { editingProjectBriefFilesStateAtom } from '../atoms/editingProjectStateAtom';

export const useBriefFiles = () => {
  const addBriefFile = useRecoilCallback(
    ({ snapshot, set }) => async (fileReference: FileReference, creativeBriefFieldKey: string) => {
      const briefFilesState = await snapshot.getPromise(editingProjectBriefFilesStateAtom);

      const updatedBriefFiles = produce(briefFilesState.briefFiles, (draft) => {
        draft[creativeBriefFieldKey] = [...draft[creativeBriefFieldKey], fileReference];
      });

      set(
        editingProjectBriefFilesStateAtom,
        produce(briefFilesState, (draft) => {
          draft.briefFiles = castDraft(updatedBriefFiles);
        }),
      );
    },
  );

  const removeBriefFile = useRecoilCallback(
    ({ snapshot, set }) => async (fileReference: FileReference, creativeBriefFieldKey: string) => {
      const briefFilesState = await snapshot.getPromise(editingProjectBriefFilesStateAtom);

      const updatedBriefFiles = produce(briefFilesState.briefFiles, (draft) => {
        draft[creativeBriefFieldKey] = draft[creativeBriefFieldKey].filter((fr) => fr.id !== fileReference.id);
      });

      set(
        editingProjectBriefFilesStateAtom,
        produce(briefFilesState, (draft) => {
          draft.briefFiles = castDraft(updatedBriefFiles);
        }),
      );
    },
  );

  const addUploadingBriefFiles = useRecoilCallback(
    ({ snapshot, set }) => async (files: ReadonlyArray<UploadingFile>, creativeBriefFieldKey: string) => {
      const briefFilesState = await snapshot.getPromise(editingProjectBriefFilesStateAtom);

      const updatedUploadingBriefFiles = produce(briefFilesState.uploadingBriefFiles, (draft) => {
        draft[creativeBriefFieldKey] = [...draft[creativeBriefFieldKey], ...files];
      });

      set(
        editingProjectBriefFilesStateAtom,
        produce(briefFilesState, (draft) => {
          draft.uploadingBriefFiles = castDraft(updatedUploadingBriefFiles);
        }),
      );
    },
  );

  const removeUploadingBriefFile = useRecoilCallback(
    ({ snapshot, set }) => async (file: File, creativeBriefFieldKey: string) => {
      const briefFilesState = await snapshot.getPromise(editingProjectBriefFilesStateAtom);

      const updatedUploadingBriefFiles = produce(briefFilesState.uploadingBriefFiles, (draft) => {
        draft[creativeBriefFieldKey] = [...draft[creativeBriefFieldKey].filter((f) => f !== file)];
      });

      set(
        editingProjectBriefFilesStateAtom,
        produce(briefFilesState, (draft) => {
          draft.uploadingBriefFiles = castDraft(updatedUploadingBriefFiles);
        }),
      );
    },
  );

  const updateUploadingFilesProgress = useRecoilCallback(
    ({ snapshot, set }) => async (fileId: string, progress: number) => {
      const briefFilesState = await snapshot.getPromise(editingProjectBriefFilesStateAtom);

      set(
        editingProjectBriefFilesStateAtom,
        produce(briefFilesState, (draft) => {
          draft.uploadingFilesProgress[fileId] = { progress };
        }),
      );
    },
  );

  const removeUploadingFilesProgress = useRecoilCallback(({ snapshot, set }) => async (fileId: string) => {
    const briefFilesState = await snapshot.getPromise(editingProjectBriefFilesStateAtom);

    set(
      editingProjectBriefFilesStateAtom,
      produce(briefFilesState, (draft) => {
        delete draft.uploadingFilesProgress[fileId];
      }),
    );
  });

  return {
    addBriefFile,
    removeBriefFile,
    addUploadingBriefFiles,
    removeUploadingBriefFile,
    updateUploadingFilesProgress,
    removeUploadingFilesProgress,
  };
};
